// src/services/api.ts

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { TokenManager } from "../utils/tokenManager";
import { DashboardApiResponse, LoanSummaryMetrics } from "../types/loan.types";
import { User } from "../types/auth.types";

// const getApiBaseUrl = (): string => {
//   const viteEnv = import.meta.env.VITE_ENVIRONMENT;
//   switch (viteEnv) {
//     case "staging":
//       return "https://api.staging.myautochek.com";
//     case "development":
//     default:
//       return "https://api-dev.autochek.africa";
//   }
// };

// const BASE_URL = getApiBaseUrl();
const BASE_URL = import.meta.env.VITE_BASE_URL
const APP_NAME = "zoho";

console.log("🌍 Using API Base URL:", BASE_URL);

export interface ApiLoanLead {
  id: string;
  firstName: string;
  lastName: string;
  status: string;
  app: string;
  updatedAt: string;
  createdAt: string;
  car?: {
    marketplacePrice?: number;
    price?: number;
  };
  loanValue?: number;
  country: string;
}

export interface ApiResponse {
  loanLeads: ApiLoanLead[];
  pagination: {
    total: number;
    currentPage: number;
    pageSize: number;
  };
}

class ApiService {
  // ─────────────────────────────────────────────
  // TOKEN REFRESH
  //
  // This is called automatically when any request gets a 401 response.
  // It uses the refresh token to silently get a new access token,
  // so the user never has to log in again just because the access token expired.
  //
  // Returns true if refresh succeeded, false if it failed (user must log in again).
  // ─────────────────────────────────────────────
  async tryRefreshToken(): Promise<boolean> {
    const refreshToken = TokenManager.getRefreshToken();
    const accessToken  = TokenManager.getToken();

    if (!refreshToken) {
      console.warn("⚠️ No refresh token available");
      return false;
    }

    try {
      console.log("🔄 Attempting token refresh...");

      const res = await fetch(`${BASE_URL}/v2/users/refresh`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-autochek-app": APP_NAME,
          "Cache-Control": "no-cache",
        },
        // Use camelCase keys — matches the API contract (login response returns camelCase)
        body: JSON.stringify({
          refreshToken,
          accessToken: accessToken ?? "",
          all: false, // false = refresh only this device, not all sessions
        }),
      });

      if (!res.ok) {
        console.warn("⚠️ Token refresh failed with status:", res.status);
        return false;
      }

      const data = await res.json();
      TokenManager.setAccessToken(data.data.accessToken);

      // Server may rotate the refresh token — persist it if provided
      if (data.data.refreshToken) {
        TokenManager.setRefreshToken(data.data.refreshToken);
      }

      console.log("✅ Token refreshed successfully");
      return true;
    } catch (err) {
      console.error("❌ Token refresh threw an error:", err);
      return false;
    }
  }

  // ─────────────────────────────────────────────
  // MAKE REQUEST
  //
  // V1 behaviour on 401: clear token, throw error, user must re-login
  // V2 behaviour on 401: try to refresh the token silently, retry the
  //                      original request — only force re-login if refresh fails
  // ─────────────────────────────────────────────
  private async makeRequest<T>(endpoint: string, isRetry = false): Promise<T> {
    const token = TokenManager.getToken();

    if (!token) {
      throw new Error("No authentication token available. Please login again.");
    }

    console.log("🚀 Making API request to:", `${BASE_URL}${endpoint}`);

    const response = await fetch(`${BASE_URL}${endpoint}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
        "x-autochek-app": APP_NAME,
      },
    });

    // ── Handle 401 (token expired or invalid) ──
    if (response.status === 401) {
      // Only try to refresh once — if this is already a retry, give up
      if (isRetry) {
        console.error(
          "❌ Token refresh did not fix the 401 — forcing re-login",
        );
        TokenManager.clearAll();
        throw new Error("authentication"); // This string is checked in App.tsx error UI
      }

      // Try to get a new access token
      const refreshed = await this.tryRefreshToken();

      if (refreshed) {
        // Retry the original request with the new token
        console.log("🔁 Retrying request after token refresh");
        return this.makeRequest<T>(endpoint, true); // isRetry=true prevents infinite loop
      } else {
        // Refresh failed — user must log in again
        TokenManager.clearAll();
        throw new Error("authentication");
      }
    }

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} - ${response.statusText}`);
    }

    const data = await response.json();
    console.log("✅ API response received");
    return data;
  }

  // ── GET /v2/users/me ────────────────────────────────────────────────
  // Returns the current user's profile, including their country code.
  // The country is used as a parameter for the dashboard API.
  // ────────────────────────────────────────────────────────────────
  async fetchCurrentUser(): Promise<User> {
    const res = await this.makeRequest<{ message: string; data: User }>("/v2/users/me");
    return res.data;
  }

  // ── GET /v1/zoho/dashboard?country=<code>&page=<n>&pageSize=<n> ─────────────
  // Returns paginated loan records for the given country.
  // ────────────────────────────────────────────────────────────────
  async fetchDashboardLoans(
    countryCode: string,
    page: number = 1,
    pageSize: number = 10,
  ): Promise<DashboardApiResponse> {
    const endpoint = `/v1/zoho/dashboard?country=${countryCode}&page=${page}&pageSize=${pageSize}`;
    return this.makeRequest<DashboardApiResponse>(endpoint);
  }

  async fetchDashboardAllLoans(
    page: number = 1,
    pageSize: number = 10,
  ): Promise<DashboardApiResponse> {
    const endpoint = `/v1/zoho/dashboard/all/loans?page=${page}&pageSize=${pageSize}`;
    return this.makeRequest<DashboardApiResponse>(endpoint);
  }

  async fetchLoans(
    countryCode: string,
    page: number = 1,
    pageSize: number = 15,
    createdToday: boolean = false,
  ): Promise<ApiResponse> {
    let endpoint = `/v1/origination/loans?sort_by=updatedAt&country=${countryCode}`;
    if (createdToday) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      endpoint += `&created_from=${encodeURIComponent(today.toISOString())}`;
    }
    return this.makeRequest<ApiResponse>(endpoint);
  }

  async fetchLoanSummaryMetrics(countryCode?: string): Promise<LoanSummaryMetrics> {
    const endpoint = countryCode
      ? `/v1/origination/loans/loan-status-summary-metrics?country=${countryCode}`
      : "/v1/origination/loans/loan-status-summary-metrics";
    return this.makeRequest<LoanSummaryMetrics>(endpoint);
  }
}

export const apiService = new ApiService();

// ── useCurrentUser ───────────────────────────────────────────────────────────────
// Fetches the logged-in user profile from /v2/users/me.
// The country field from this response is then used as the
// `country` parameter for the dashboard API.
// ────────────────────────────────────────────────────────────────
export const useCurrentUser = () => {
  return useQuery({
    queryKey: ["currentUser"],
    queryFn: () => apiService.fetchCurrentUser(),
    // User profile changes rarely — cache for 10 minutes
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 2,
  });
};

// ── useDashboardLoans ───────────────────────────────────────────────────────
// Fetches paginated loans from /v1/zoho/dashboard with the user's country.
// Disabled until country is known (enabled: !!countryCode).
// ────────────────────────────────────────────────────────────────
export const useDashboardLoans = (
  countryCode: string,
  page: number = 1,
  pageSize: number = 10,
) => {
  return useQuery({
    queryKey: ["dashboardLoans", countryCode, page, pageSize],
    queryFn: () => apiService.fetchDashboardLoans(countryCode, page, pageSize),
    enabled: !!countryCode,
    staleTime: 15000, // Consider data fresh for 15 seconds
    refetchInterval: 30000, // Auto-refresh every 30 seconds
    refetchOnWindowFocus: false, // Prevent "storms" when switching tabs
    refetchOnReconnect: true,
    retry: 3,
    retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

export const useDashboardAllLoans = (
  page: number = 1,
  pageSize: number = 10,
  enabled: boolean = false,
) => {
  return useQuery({
    queryKey: ["dashboardAllLoans", page, pageSize],
    queryFn: () => apiService.fetchDashboardAllLoans(page, pageSize),
    enabled,
    staleTime: 15000, // Consider data fresh for 15 seconds
    refetchInterval: 30000, // Auto-refresh every 30 seconds
    refetchOnWindowFocus: false, // Prevent "storms" when switching tabs
    refetchOnReconnect: true,
    retry: 3,
    retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

// ── useLoans (legacy) ──────────────────────────────────────────────────────────
// React Query hooks — unchanged from your original
export const useLoans = (
  countryCode: string,
  page: number = 1,
  pageSize: number = 15,
  createdToday: boolean = false,
) => {
  return useQuery({
    queryKey: ["loans", countryCode, page, pageSize, createdToday],
    queryFn: () =>
      apiService.fetchLoans(countryCode, page, pageSize, createdToday),
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: 3,
    retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

export const useLoanSummaryMetrics = (countryCode?: string) => {
  return useQuery({
    queryKey: ["loanSummaryMetrics", countryCode],
    queryFn: () => apiService.fetchLoanSummaryMetrics(countryCode),
    // Refresh summary metrics periodically
    staleTime: 30000,
    refetchInterval: 60000,
    refetchOnWindowFocus: true,
  });
};

export const useLoanQueryManager = () => {
  const queryClient = useQueryClient();

  const invalidateLoans = (countryCode?: string) => {
    if (countryCode) {
      queryClient.invalidateQueries({ queryKey: ["loans", countryCode] });
    } else {
      queryClient.invalidateQueries({ queryKey: ["loans"] });
    }
  };

  const prefetchLoans = async (countryCode: string, pageSize: number = 15) => {
    await queryClient.prefetchQuery({
      queryKey: ["loans", countryCode, pageSize],
      queryFn: () => apiService.fetchLoans(countryCode, pageSize),
      staleTime: 10000,
    });
  };

  return { invalidateLoans, prefetchLoans };
};